import { Info, LayoutGrid } from 'lucide-react'
import { useState } from 'react'

import {
  BENEFIT_ICONS,
  BENEFIT_ICON_OPTIONS,
  type BenefitIconName,
} from '@/app/components/settings/benefit-icon-catalog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card'
import { Input } from '@/app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { Textarea } from '@/app/components/ui/textarea'


export interface PurchaseBenefitBlock {
  icon: BenefitIconName
  title: string
  description: string
}

export interface PurchaseBenefitsFormState {
  title: string
  subtitle: string
  blocks: PurchaseBenefitBlock[]
}

type TouchedFields = Record<string, boolean>

interface PurchaseBenefitsSettingsCardProps {
  value: PurchaseBenefitsFormState
  canEdit?: boolean
  onChange: (value: PurchaseBenefitsFormState) => void
}

export function PurchaseBenefitsSettingsCard({
  value,
  canEdit = true,
  onChange, }: PurchaseBenefitsSettingsCardProps) {
  const [touched, setTouched] = useState<TouchedFields>({})

  const markTouched = (field: string) => {
    setTouched((current) => ({ ...current, [field]: true }))
  }

  const hasError = (field: string, value: string) => Boolean(touched[field] && !value.trim())

  const updateBlock = (
    index: number,
    changes: Partial<PurchaseBenefitBlock>,
  ) => {
    onChange({
      ...value,
      blocks: value.blocks.map((block, blockIndex) =>
        blockIndex === index
          ? { ...block, ...changes }
          : block,
      ),
    })
  }

  return (
    <Card className="rounded-lg" aria-labelledby="purchase-benefits-settings-title">
      <CardHeader>
        <CardTitle id="purchase-benefits-settings-title" className="flex items-center gap-2 text-base">
          <LayoutGrid className="h-5 w-5 text-primary" />
          Diferenciales
        </CardTitle>
        <CardDescription>
          Configura el encabezado y los cuatro beneficios que se mostrarán en esta sección.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div
          role="note"
          className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm text-card-foreground"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p>
            Esta configuración es una vista previa. Puedes probar los campos, pero sus cambios todavía no se guardan.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <RequiredTextField
            id="purchase-benefits-title"
            label="Título de la sección"
            value={value.title}
            disabled={!canEdit}
            error={hasError('title', value.title)}
            onBlur={() => markTouched('title')}
            onChange={(title) => onChange({ ...value, title })}
          />
          <RequiredTextField
            id="purchase-benefits-subtitle"
            label="Subtítulo de la sección"
            value={value.subtitle}
            disabled={!canEdit}
            error={hasError('subtitle', value.subtitle)}
            onBlur={() => markTouched('subtitle')}
            onChange={(subtitle) => onChange({ ...value, subtitle })}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {value.blocks.map((block, index) => {
            const titleField = `blocks.${index}.title`
            const descriptionField = `blocks.${index}.description`
            const blockNumber = index + 1

            return (
              <fieldset
                key={blockNumber}
                className="space-y-4 rounded-lg border border-card-border bg-card-foreground/[0.02] p-4"
              >
                <legend className="px-1 text-sm font-semibold text-card-foreground">
                  Bloque {blockNumber}
                </legend>

                <div className="space-y-2">
                  <label
                    htmlFor={`purchase-benefit-icon-${blockNumber}`}
                    className="text-sm font-medium text-card-foreground"
                  >
                    Icono <RequiredMark />
                  </label>
                  <Select
                    value={block.icon}
                    disabled={!canEdit}
                    onValueChange={(icon: BenefitIconName) => updateBlock(index, { icon })}
                  >
                    <SelectTrigger
                      id={`purchase-benefit-icon-${blockNumber}`}
                      aria-label={`Icono del bloque ${blockNumber}`}
                      aria-required="true"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BENEFIT_ICON_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <BenefitIcon name={option.value} className="h-4 w-4" />
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <RequiredTextField
                  id={`purchase-benefit-title-${blockNumber}`}
                  label="Título"
                  value={block.title}
                  disabled={!canEdit}
                  error={hasError(titleField, block.title)}
                  onBlur={() => markTouched(titleField)}
                  onChange={(title) => updateBlock(index, { title })}
                />

                <RequiredTextArea
                  id={`purchase-benefit-description-${blockNumber}`}
                  label="Texto descriptivo"
                  value={block.description}
                  disabled={!canEdit}
                  error={hasError(descriptionField, block.description)}
                  onBlur={() => markTouched(descriptionField)}
                  onChange={(description) => updateBlock(index, { description })}
                />
              </fieldset>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

interface RequiredFieldProps {
  id: string
  label: string
  value: string
  disabled: boolean
  error: boolean
  onBlur: () => void
  onChange: (value: string) => void
}

function RequiredTextField({
  id,
  label,
  value,
  disabled,
  error,
  onBlur,
  onChange,
}: RequiredFieldProps) {
  const errorId = `${id}-error`

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium text-card-foreground">
        {label} <RequiredMark />
      </label>
      <Input
        id={id}
        value={value}
        disabled={disabled}
        aria-required="true"
        aria-invalid={error}
        aria-describedby={error ? errorId : undefined}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && <RequiredError id={errorId} />}
    </div>
  )
}

function RequiredTextArea({
  id,
  label,
  value,
  disabled,
  error,
  onBlur,
  onChange,
}: RequiredFieldProps) {
  const errorId = `${id}-error`

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium text-card-foreground">
        {label} <RequiredMark />
      </label>
      <Textarea
        id={id}
        value={value}
        disabled={disabled}
        aria-required="true"
        aria-invalid={error}
        aria-describedby={error ? errorId : undefined}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && <RequiredError id={errorId} />}
    </div>
  )
}

function RequiredMark() {
  return <span className="text-destructive" aria-hidden="true">*</span>
}

function RequiredError({ id }: { id: string }) {
  return (
    <p id={id} role="alert" className="text-xs text-destructive">
      Este campo es obligatorio.
    </p>
  )
}

function BenefitIcon({ name, className }: { name: BenefitIconName; className?: string }) {
  const Icon = BENEFIT_ICONS[name]

  return <Icon className={className} aria-hidden="true" />
}
